import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const { t, language, changeLanguage, availableLanguages } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'hero', label: t('nav.home') },
    { id: 'services', label: t('nav.services') },
    { id: 'process', label: t('nav.process') },
    { id: 'why-us', label: t('nav.whyUs') },
    { id: 'contact', label: t('nav.contact') },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0A0A0A] text-[#A1A1AA] text-xs py-2 px-4 text-center border-b border-white/5">
        <span>We Speak German, English, Turkish, Russian</span>
      </div>

      {/* Main Navigation */}
      <nav
        data-testid="navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/90 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
        }`}
        style={{ top: isScrolled ? 0 : '32px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button
              onClick={() => scrollToSection('hero')}
              className="flex items-center"
              data-testid="logo"
            >
              <img 
                src="/images/logo.png" 
                alt="ALKA" 
                className="h-8 md:h-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span className="text-2xl md:text-3xl font-light tracking-[0.3em] text-white hover:text-[#B8860B] transition-colors hidden">
                ALKA
              </span>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className="text-sm text-white/80 hover:text-[#B8860B] transition-colors tracking-wide"
                  data-testid={`nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Language Switcher & CTA */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="flex items-center space-x-1 text-xs">
                {availableLanguages.map((lang, idx) => (
                  <React.Fragment key={lang}>
                    <button
                      onClick={() => changeLanguage(lang)}
                      className={`px-2 py-1 transition-colors ${
                        language === lang
                          ? 'text-[#B8860B]'
                          : 'text-white/60 hover:text-white'
                      }`}
                      data-testid={`lang-${lang}`}
                    >
                      {lang.toUpperCase()}
                      {lang === 'ru' && '*'}
                    </button>
                    {idx < availableLanguages.length - 1 && (
                      <span className="text-white/20">|</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-transparent border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black px-5 py-2 rounded-full text-sm tracking-wide transition-all duration-300"
                data-testid="nav-cta"
              >
                {t('nav.contact')}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-white p-2"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-white/10 transition-all duration-300 ${
            isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        >
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left text-white/80 hover:text-[#B8860B] py-2 transition-colors"
                data-testid={`mobile-nav-${link.id}`}
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center space-x-2 pt-4 border-t border-white/10">
              {availableLanguages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    language === lang
                      ? 'bg-[#B8860B] text-black'
                      : 'text-white/60 hover:text-white'
                  }`}
                  data-testid={`mobile-lang-${lang}`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
